import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import AddTipModal from './AddTipModal';
import EditTipModal from './EditTipModal';
import AICoachingTipsModal from './AICoachingTipsModal';
import ProTipService from '../../../services/proTipService';

const TipCategoryCard = ({ 
  category, 
  proTips, 
  isExpanded, 
  onToggle, 
  onTipAdded, 
  onTipUpdated, 
  onTipRemoved, 
  user, 
  onError 
}) => {
  const [showAddTip, setShowAddTip] = useState(false);
  const [showEditTip, setShowEditTip] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [selectedTip, setSelectedTip] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleAddTip = () => {
    setShowAddTip(true);
  };

  const handleEditTip = (tip) => {
    setSelectedTip(tip);
    setShowEditTip(true);
  };

  const handleAIGenerate = () => {
    setShowAIModal(true);
  };

  const handleTipCreated = async () => {
    setShowAddTip(false);
    onTipAdded?.();
  };

  const handleAITipsCreated = async () => {
    setShowAIModal(false);
    onTipAdded?.();
  };

  const handleTipUpdated = async () => {
    setShowEditTip(false);
    setSelectedTip(null);
    onTipUpdated?.();
  };

  const handleDeleteTip = async (tipId) => {
    if (!window.confirm('Are you sure you want to delete this tip?')) {
      return;
    }

    try {
      setIsLoading(true);
      onError?.(null);

      const { error } = await ProTipService?.deleteProTip(tipId);

      if (error) {
        onError?.(`Failed to delete tip: ${error}`);
        return;
      }

      // Success - refresh parent
      onTipRemoved?.();
    } catch (err) {
      onError?.('An unexpected error occurred while deleting the tip.');
    } finally {
      setIsLoading(false);
    }
  };

  const getTotalTipsCount = () => {
    return proTips?.length || 0;
  };

  return (
    <>
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl overflow-hidden">
        {/* Category Header */}
        <div className="p-6">
          <button
            onClick={onToggle}
            className="w-full flex items-center justify-between hover:bg-white hover:bg-opacity-10 -m-2 p-2 rounded-lg transition-colors duration-200"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
                <Icon name={category?.icon} size={20} className="text-white" />
              </div>
              <div className="text-left">
                <h3 className="text-lg font-heading font-semibold text-white">
                  {category?.label}
                </h3>
                <p className="text-amber-200 text-sm font-body">
                  {category?.description} • {getTotalTipsCount()} tips
                </p>
              </div>
            </div>
            <Icon 
              name={isExpanded ? 'ChevronUp' : 'ChevronDown'} 
              size={20} 
              className="text-amber-200" 
            />
          </button>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t border-white border-opacity-20">
            <div className="p-6">
              {/* Add Tip Button */}
              <div className="mb-4 flex items-center space-x-2">
                <Button
                  variant="outline"
                  onClick={handleAddTip}
                  iconName="Plus"
                  iconPosition="left"
                  disabled={isLoading}
                  className="border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-white rounded-lg"
                >
                  Add Tip
                </Button>
                <Button
                  variant="outline"
                  onClick={handleAIGenerate}
                  iconName="Sparkles"
                  iconPosition="left"
                  disabled={isLoading}
                  className="border-violet-400 text-violet-400 hover:bg-violet-500 hover:text-white rounded-lg"
                >
                  AI Generate
                </Button>
              </div>

              {/* Tips List */}
              <div className="space-y-3">
                {proTips?.length === 0 ? (
                  <div className="text-center py-8 text-amber-200">
                    <Icon name="Lightbulb" size={32} className="mx-auto mb-3 opacity-50" />
                    <p className="font-body">No tips added yet</p>
                    <p className="text-sm text-amber-300 mt-1">Click "Add Tip" to get started</p>
                  </div>
                ) : (
                  proTips
                    ?.sort((a, b) => (a?.display_order || 0) - (b?.display_order || 0))
                    ?.map((tip, index) => (
                      <div key={tip?.id} className="bg-white bg-opacity-10 rounded-xl p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="text-xs text-amber-300 bg-amber-900 bg-opacity-50 px-2 py-1 rounded-full">
                                {index + 1}
                              </span>
                              <h4 className="font-medium text-white font-caption truncate">
                                {tip?.title}
                              </h4>
                            </div>
                            <div className="text-amber-100 text-sm font-body leading-relaxed">
                              {tip?.content?.length > 150 
                                ? `${tip?.content?.substring(0, 150)}...` 
                                : tip?.content
                              }
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-1 ml-3">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditTip(tip)}
                              iconName="Edit2"
                              disabled={isLoading}
                              className="text-amber-300 hover:text-white hover:bg-amber-600 rounded-lg"
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteTip(tip?.id)}
                              iconName="Trash2"
                              disabled={isLoading}
                              className="text-red-400 hover:text-white hover:bg-red-600 rounded-lg"
                            />
                          </div>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Tip Modal */}
      {showAddTip && (
        <AddTipModal
          isOpen={showAddTip}
          onClose={() => setShowAddTip(false)}
          onTipCreated={handleTipCreated}
          category={category}
          user={user}
          onError={onError}
        />
      )}

      {/* Edit Tip Modal */}
      {showEditTip && selectedTip && (
        <EditTipModal
          isOpen={showEditTip}
          onClose={() => {
            setShowEditTip(false);
            setSelectedTip(null);
          }}
          onTipUpdated={handleTipUpdated}
          tip={selectedTip}
          category={category}
          user={user}
          onError={onError}
        />
      )}

      {/* AI Coaching Tips Modal */}
      {showAIModal && (
        <AICoachingTipsModal
          isOpen={showAIModal}
          onClose={() => setShowAIModal(false)}
          onTipsCreated={handleAITipsCreated}
          category={category}
          user={user}
          onError={onError}
        />
      )}
    </>
  );
};

export default TipCategoryCard;